import { useState, useCallback, useRef, useEffect } from "react";
import { FolderPlus, Upload, Folder, Trash2, Pencil, X, ChevronRight, Home, MousePointerClick } from "lucide-react";
import { MediaProvider, useMedia, type CreateFolderInput, type MediaFolder } from "./MediaProvider";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";
import { HierarchyPicker } from "../../components/HierarchyPicker/HierarchyPicker";
import { type PermissionGroup, getAncestorIds } from "../../components/HierarchyPicker/types";
import { MediaBrowser } from "../../components/MediaBrowser/MediaBrowser";

type GroupWithDepth = PermissionGroup & { depth: number };

// --- File Detail Panel ---

const FileDetailPanel = () => {
  const { files, selectedFileId, selectFile, deleteFile } = useMedia();
  const [isDeleting, setIsDeleting] = useState(false);

  const file = files.find((f) => f.id === selectedFileId);
  if (!file) return null;

  const isImage = file.contentType.startsWith("image/");
  const isVideo = file.contentType.startsWith("video/");

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this file?")) return;
    setIsDeleting(true);
    try {
      await deleteFile(file.id);
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <h3 className="font-medium text-white">Details</h3>
        <button onClick={() => selectFile(null)} className="p-1 hover:bg-zinc-800 rounded">
          <X size={16} className="text-zinc-400" />
        </button>
      </div>

      <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img src={file.downloadUrl} alt={file.originalFilename} className="max-w-full max-h-full object-contain" />
        ) : isVideo ? (
          <video src={file.downloadUrl} className="max-w-full max-h-full" controls />
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide">Filename</label>
          <p className="text-sm text-white mt-1 break-all">{file.originalFilename}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Type</label>
            <p className="text-sm text-white mt-1">{isImage ? "Image" : "Video"}</p>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Size</label>
            <p className="text-sm text-white mt-1">{formatSize(file.sizeBytes)}</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide">Uploaded</label>
          <p className="text-sm text-white mt-1">{formatDate(file.createdAt)}</p>
        </div>

        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Tags</label>
          {file.tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {file.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-sm text-zinc-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No tags</p>
          )}
        </div>

        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide">Download URL</label>
          <a
            href={file.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-400 hover:text-blue-300 mt-1 block truncate"
          >
            Open in new tab
          </a>
        </div>
      </div>

      {file.canEdit ? (
        <div className="p-4 border-t border-zinc-800">
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="w-full px-4 py-2 bg-red-600/20 border border-red-600/50 text-red-400 rounded-lg hover:bg-red-600/30 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            {isDeleting ? "Deleting..." : "Delete File"}
          </button>
        </div>
      ) : (
        <div className="p-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 text-center">Read-only — you cannot modify this file.</p>
        </div>
      )}
    </div>
  );
};

// --- Folder Detail Panel ---

const FolderDetailPanel = () => {
  const { folders, selectedFolderId, selectFolder, currentFolder, breadcrumbs, updateFolder, navigateToFolder } =
    useMedia();
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [groups, setGroups] = useState<PermissionGroup[]>([]);

  // Draft state — applied via Save button.
  const [name, setName] = useState("");
  const [locationGroupId, setLocationGroupId] = useState<string | null>(null);
  const [descendantsCanView, setDescendantsCanView] = useState(false);
  const [descendantsCanEdit, setDescendantsCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const folder = folders.find((f) => f.id === selectedFolderId);

  useEffect(() => {
    if (!folder) return;
    setName(folder.name);
    setLocationGroupId(folder.locationGroupId);
    setDescendantsCanView(folder.descendantsCanView);
    setDescendantsCanEdit(folder.descendantsCanEdit);
    setError(null);
  }, [folder?.id]);

  useEffect(() => {
    if (!currentTenantId) return;
    const load = async () => {
      try {
        const data = await apiClient.get<{ groups: GroupWithDepth[] }>(
          `/api/groups?tenantId=${currentTenantId}&permission=media.manage`,
        );
        setGroups(data.groups);
      } catch (err: any) {
        console.error("Failed to load groups:", err);
      }
    };
    load();
  }, [apiClient, currentTenantId]);

  if (!folder) return null;

  const pickerGroups = currentFolder
    ? groups.filter(
        (g) =>
          g.id === currentFolder.locationGroupId ||
          getAncestorIds(groups, g.id).includes(currentFolder.locationGroupId),
      )
    : groups;

  const isEditable = folder.canEdit;

  const handleEditToggle = (checked: boolean) => {
    setDescendantsCanEdit(checked);
    if (checked) setDescendantsCanView(true);
  };

  const handleViewToggle = (checked: boolean) => {
    if (descendantsCanEdit) return;
    setDescendantsCanView(checked);
  };

  const isDirty =
    name.trim() !== folder.name ||
    locationGroupId !== folder.locationGroupId ||
    descendantsCanView !== folder.descendantsCanView ||
    descendantsCanEdit !== folder.descendantsCanEdit;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || !locationGroupId) return;
    setSaving(true);
    setError(null);
    try {
      await updateFolder(folder.id, {
        name: trimmed,
        locationGroupId,
        descendantsCanView,
        descendantsCanEdit,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <h3 className="font-medium text-white">Folder Details</h3>
        <button onClick={() => selectFolder(null)} className="p-1 hover:bg-zinc-800 rounded">
          <X size={16} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Location</label>
          <div className="flex items-center gap-1 text-sm flex-wrap">
            <button
              onClick={() => navigateToFolder(null)}
              className="flex items-center gap-1 text-zinc-400 hover:text-white"
            >
              <Home size={12} />
              <span>Root</span>
            </button>
            {breadcrumbs.map((b) => (
              <div key={b.id} className="flex items-center gap-1">
                <ChevronRight size={12} className="text-zinc-600" />
                <button onClick={() => navigateToFolder(b.id)} className="text-zinc-400 hover:text-white">
                  {b.name}
                </button>
              </div>
            ))}
            <ChevronRight size={12} className="text-zinc-600" />
            <span className="text-white font-medium">{folder.name}</span>
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide mb-1 block">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isEditable || saving}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm disabled:opacity-50"
          />
        </div>

        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide mb-1 block">Applies To</label>
          <HierarchyPicker
            groups={pickerGroups}
            selectedId={locationGroupId}
            onChange={setLocationGroupId}
            placeholder="Select location..."
            disabled={!isEditable || saving}
          />
          <p className="text-xs text-zinc-500 mt-1">
            {currentFolder
              ? "Must be the parent's location or a child of it."
              : "Folder is owned by this location group."}
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-xs text-zinc-500 uppercase tracking-wide block">Descendant access</label>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={descendantsCanView}
              onChange={(e) => handleViewToggle(e.target.checked)}
              disabled={!isEditable || saving || descendantsCanEdit}
              className="mt-0.5"
            />
            <span>
              Descendant groups can view
              <span className="block text-xs text-zinc-500">
                Users in child groups can see folder contents (read-only).
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={descendantsCanEdit}
              onChange={(e) => handleEditToggle(e.target.checked)}
              disabled={!isEditable || saving}
              className="mt-0.5"
            />
            <span>
              Descendant groups can edit
              <span className="block text-xs text-zinc-500">
                Users in child groups can upload, rename, and delete (implies view).
              </span>
            </span>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Files</label>
            <p className="text-sm text-white mt-1">{folder.fileCount}</p>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Subfolders</label>
            <p className="text-sm text-white mt-1">{folder.subfolderCount}</p>
          </div>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>

      <div className="p-4 border-t border-zinc-800">
        {isEditable ? (
          <button
            onClick={handleSave}
            disabled={!isDirty || !name.trim() || !locationGroupId || saving}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm rounded-lg"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        ) : (
          <p className="text-xs text-zinc-500 text-center">Read-only — you cannot modify this folder.</p>
        )}
      </div>
    </div>
  );
};

// --- Detail Placeholder (shown when nothing is selected) ---

const DetailPlaceholder = () => (
  <div className="flex flex-col h-full">
    <div className="p-4 border-b border-zinc-800">
      <h3 className="font-medium text-white">Details</h3>
    </div>
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-3">
      <MousePointerClick size={36} className="text-zinc-700" />
      <p className="text-sm text-zinc-400">Select a file or folder to view its details.</p>
      <div className="text-xs text-zinc-500 space-y-1">
        <p>Single-click selects an item.</p>
        <p>Double-click a folder to open it.</p>
      </div>
    </div>
  </div>
);

// --- Upload Zone ---

const UploadZone = () => {
  const { uploadFile, uploading, currentFolder, currentFolderId } = useMedia();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ name: string; status: "uploading" | "done" | "error" }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canUpload = !!currentFolderId && !!currentFolder?.canUpload;

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (canUpload) setIsDragging(true);
    },
    [canUpload],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadAll = async (files: File[]) => {
    for (const file of files) {
      setUploadProgress((prev) => [...prev, { name: file.name, status: "uploading" }]);
      try {
        await uploadFile(file);
        setUploadProgress((prev) => prev.map((p) => (p.name === file.name ? { ...p, status: "done" } : p)));
      } catch {
        setUploadProgress((prev) => prev.map((p) => (p.name === file.name ? { ...p, status: "error" } : p)));
      }
    }
    setTimeout(() => setUploadProgress([]), 3000);
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!canUpload) return;
      const files = Array.from(e.dataTransfer.files).filter(
        (f) => f.type.startsWith("image/") || f.type.startsWith("video/"),
      );
      await uploadAll(files);
    },
    [canUpload, uploadFile],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      await uploadAll(files);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [uploadFile],
  );

  if (!currentFolderId) {
    return (
      <div className="border-2 border-dashed border-zinc-800 rounded-lg p-8 text-center bg-zinc-900/40">
        <Folder size={32} className="mx-auto mb-3 text-zinc-700" />
        <p className="text-sm text-zinc-500">Open a folder to upload files.</p>
      </div>
    );
  }

  if (!canUpload) {
    return (
      <div className="border-2 border-dashed border-zinc-800 rounded-lg p-8 text-center bg-zinc-900/40">
        <Upload size={32} className="mx-auto mb-3 text-zinc-700" />
        <p className="text-sm text-zinc-500">You don’t have upload access in this folder.</p>
      </div>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
          isDragging ? "border-blue-500 bg-blue-500/10" : "border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50"
        }`}
      >
        <Upload size={32} className={`mx-auto mb-3 ${isDragging ? "text-blue-400" : "text-zinc-500"}`} />
        <p className={`text-sm ${isDragging ? "text-blue-400" : "text-zinc-400"}`}>
          {uploading ? "Uploading..." : "Drop files here or click to upload"}
        </p>
        <p className="text-xs text-zinc-600 mt-1">Images and videos only · Max 500 MB</p>
      </div>

      {uploadProgress.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-zinc-800 border border-zinc-700 rounded-lg p-4 shadow-xl max-w-sm">
          <p className="text-sm font-medium text-white mb-2">Uploading files</p>
          <div className="space-y-2">
            {uploadProgress.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span
                  className={`w-2 h-2 rounded-full ${
                    p.status === "uploading"
                      ? "bg-blue-500 animate-pulse"
                      : p.status === "done"
                        ? "bg-green-500"
                        : "bg-red-500"
                  }`}
                />
                <span className="text-zinc-300 truncate">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// --- Create Folder Dialog ---

type CreateFolderDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateFolderInput) => Promise<unknown>;
  parentFolder: MediaFolder | null;
};

const CreateFolderDialog = ({ isOpen, onClose, onCreate, parentFolder }: CreateFolderDialogProps) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [name, setName] = useState("");
  const [locationGroupId, setLocationGroupId] = useState<string | null>(null);
  const [descendantsCanView, setDescendantsCanView] = useState(false);
  const [descendantsCanEdit, setDescendantsCanEdit] = useState(false);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !currentTenantId) return;
    setName("");
    setError(null);
    setDescendantsCanView(false);
    setDescendantsCanEdit(false);

    const load = async () => {
      try {
        const data = await apiClient.get<{ groups: GroupWithDepth[] }>(
          `/api/groups?tenantId=${currentTenantId}&permission=media.manage`,
        );
        setGroups(data.groups);
        const highest = [...data.groups].sort((a, b) => a.depth - b.depth)[0];
        setLocationGroupId(parentFolder?.locationGroupId ?? highest?.id ?? null);
      } catch (err: any) {
        setError(err.message);
      }
    };
    load();
  }, [isOpen, apiClient, currentTenantId, parentFolder?.locationGroupId]);

  if (!isOpen) return null;

  const pickerGroups = parentFolder
    ? groups.filter(
        (g) =>
          g.id === parentFolder.locationGroupId || getAncestorIds(groups, g.id).includes(parentFolder.locationGroupId),
      )
    : groups;

  const handleEditToggle = (checked: boolean) => {
    setDescendantsCanEdit(checked);
    if (checked) setDescendantsCanView(true);
  };

  const handleViewToggle = (checked: boolean) => {
    if (descendantsCanEdit) return;
    setDescendantsCanView(checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !locationGroupId) return;
    setIsCreating(true);
    setError(null);
    try {
      await onCreate({ name: name.trim(), locationGroupId, descendantsCanView, descendantsCanEdit });
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-white mb-1">Create Folder</h2>
        <p className="text-xs text-zinc-500 mb-4">
          {parentFolder ? `Inside "${parentFolder.name}"` : "At the root of this tenant"}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 uppercase tracking-wide mb-1 block">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Folder name"
              autoFocus
              className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="text-xs text-zinc-400 uppercase tracking-wide mb-1 block">Location</label>
            <HierarchyPicker
              groups={pickerGroups}
              selectedId={locationGroupId}
              onChange={setLocationGroupId}
              placeholder="Select location..."
            />
            <p className="text-xs text-zinc-500 mt-1">
              {parentFolder
                ? "Must be the parent's location or a child of it."
                : "Folder will be owned by this location group."}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-zinc-400 uppercase tracking-wide block">Descendant access</label>
            <label className="flex items-start gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={descendantsCanView}
                onChange={(e) => handleViewToggle(e.target.checked)}
                disabled={descendantsCanEdit}
                className="mt-0.5"
              />
              <span>
                Descendant groups can view
                <span className="block text-xs text-zinc-500">
                  Users in child groups can see folder contents (read-only).
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={descendantsCanEdit}
                onChange={(e) => handleEditToggle(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Descendant groups can edit
                <span className="block text-xs text-zinc-500">
                  Users in child groups can upload, rename, and delete (implies view).
                </span>
              </span>
            </label>
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-zinc-400 hover:text-white">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !locationGroupId || isCreating}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg"
            >
              {isCreating ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Main Content ---

const MediaContent = () => {
  const {
    createFolder,
    deleteFolder,
    renameFolder,
    selectedFileId,
    selectFile,
    selectedFolderId,
    selectFolder,
    currentFolderId,
    currentFolder,
  } = useMedia();
  const { currentTenantId } = useTenant();

  const [showCreateFolder, setShowCreateFolder] = useState(false);

  const canCreateFolder = currentFolderId ? !!currentFolder?.canCreateChild : true;

  const handleDeleteFolder = async (id: string) => {
    if (!confirm("Are you sure you want to delete this folder? It must be empty.")) return;
    try {
      await deleteFolder(id);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRenameFolder = async (folder: MediaFolder) => {
    const newName = prompt("Enter new folder name:", folder.name);
    if (!newName || newName === folder.name) return;
    try {
      await renameFolder(folder.id, newName);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const renderFolderMenu = (folder: MediaFolder, close: () => void) => (
    <>
      <button
        onClick={() => {
          handleRenameFolder(folder);
          close();
        }}
        className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
      >
        <Pencil size={14} />
        Rename
      </button>
      <button
        onClick={() => {
          handleDeleteFolder(folder.id);
          close();
        }}
        className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-zinc-700 flex items-center gap-2"
      >
        <Trash2 size={14} />
        Delete
      </button>
    </>
  );

  const clearSelection = () => {
    selectFile(null);
    selectFolder(null);
  };

  return (
    <div className="flex-1 flex overflow-hidden" onClick={clearSelection}>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Media Library</h1>
              <p className="text-sm text-zinc-400 mt-1">Upload and manage your media files</p>
            </div>
            <button
              onClick={() => setShowCreateFolder(true)}
              disabled={!canCreateFolder}
              title={canCreateFolder ? undefined : "You don’t have permission to create folders here"}
              className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FolderPlus size={16} />
              New Folder
            </button>
          </div>
        </div>

        <MediaBrowser
          renderFolderMenu={renderFolderMenu}
          onFileSelect={(file) => selectFile(file?.id ?? null)}
          onFolderSelect={(folder) => selectFolder(folder?.id ?? null)}
          onBackgroundClick={clearSelection}
        >
          <UploadZone />
        </MediaBrowser>
      </div>

      <div
        className="w-80 shrink-0 border-l border-zinc-800 bg-zinc-900 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {selectedFileId ? <FileDetailPanel /> : selectedFolderId ? <FolderDetailPanel /> : <DetailPlaceholder />}
      </div>

      <CreateFolderDialog
        key={currentTenantId ?? "no-tenant"}
        isOpen={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        onCreate={createFolder}
        parentFolder={currentFolder}
      />
    </div>
  );
};

// --- Main Screen ---

export default () => {
  return (
    <MediaProvider>
      <MediaContent />
    </MediaProvider>
  );
};
